import { Breadcrumb, Event } from '@sentry/browser';

/**
 * Keeps user file identifiers out of Sentry.
 *
 * This is not tidiness. Google's restricted-scope policy turns on whether an
 * app "has the ability to access data from or through a third-party server":
 * an app that keeps restricted data entirely client-side avoids the annual
 * third-party security assessment. Fadila has no backend for user data - audio
 * streams straight from Google to the browser and the library lives in the
 * user's own drive - so the claim is true of the app proper.
 *
 * Sentry was the hole in it. Its default breadcrumb integration records every
 * fetch and XHR *url*, and every Drive read is
 * `drive/v3/files/<fileId>?alt=media`, so file ids were leaving the browser on
 * any error report. `extra.raw` in snackbar.ts made it worse by attaching the
 * raw gapi error object, which embeds the request url and sometimes the
 * response body.
 *
 * So: scrub every string on the way out, recursively. Losing the exact id
 * costs a little debugging detail; keeping it costs a security assessment.
 */

const REDACTIONS: Array<[RegExp, string]> = [
  // Google Drive file ids, in urls and in error bodies alike.
  [/(\/drive\/v\d\/files\/)[A-Za-z0-9_-]{10,}/g, '$1[id]'],
  [/(\/upload\/drive\/v\d\/files\/)[A-Za-z0-9_-]{10,}/g, '$1[id]'],
  // Microsoft Graph item ids and approot paths.
  [/(\/drive\/items\/)[A-Za-z0-9!._-]{6,}/g, '$1[id]'],
  [/(\/drive\/special\/approot:)[^\s'"]*/g, '$1[path]'],
  // Dropbox paths are sent in request bodies rather than urls.
  [/("path"\s*:\s*")[^"]*/g, '$1[path]'],
  // Drive search queries name folders and, via `name contains`, whatever the
  // user typed.
  [/([?&]q=)[^&\s'"]+/g, '$1[query]'],
  // Anything token-shaped, wherever it turns up.
  [/([?&](?:access_token|id_token|refresh_token|token)=)[^&\s'"]+/gi, '$1[redacted]'],
  [/(Bearer\s+)[A-Za-z0-9._~+/-]+=*/g, '$1[redacted]'],
];

function scrubString(value: string) {
  return REDACTIONS.reduce(
    (acc, [pattern, replacement]) => acc.replace(pattern, replacement),
    value,
  );
}

/**
 * Walks anything Sentry is about to send. Depth-capped because error objects
 * from gapi are deep and occasionally cyclic, and a stack overflow inside
 * beforeSend would lose the report entirely.
 */
function scrub(value: any, depth = 0): any {
  if (depth > 6) {
    return value;
  }
  if (typeof value === 'string') {
    return scrubString(value);
  }
  if (Array.isArray(value)) {
    return value.map(item => scrub(item, depth + 1));
  }
  if (value && typeof value === 'object') {
    const out: any = {};
    for (const key of Object.keys(value)) {
      try {
        out[key] = scrub(value[key], depth + 1);
      }
      catch {
        // Getters on gapi responses can throw. One bad key is not worth the
        // whole report.
        out[key] = '[unreadable]';
      }
    }
    return out;
  }
  return value;
}

export function beforeBreadcrumb(breadcrumb: Breadcrumb) {
  return scrub(breadcrumb) as Breadcrumb;
}

export function beforeSend(event: Event) {
  return scrub(event) as Event;
}
